import { useState, useEffect } from 'react';

import { useMediaClient } from '@atlaskit/media-client-react';

import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { LoomVideoDurationsQuery } from '../LoomVideoDurationsQuery.agggraphql.ts';
import type { LoomVideoDurationsQuery as LoomVideoDurationsQueryType } from '../__types__/LoomVideoDurationsQuery.ts';

export const getLoomARI = ({
	cloudId,
	activationId,
	videoId,
}: {
	cloudId: string;
	activationId: string;
	videoId: string;
}) => `ari:cloud:loom:${cloudId}:video/activation/${activationId}/${videoId}`;

export const useLoadMediaFileDurations = (mediaFiles: string[], collectionName: string) => {
	const [mediaVideoDurations, setMediaVideoDurations] = useState(0);
	const [isLoadingMediaDurations, setIsLoadingMediaDurations] = useState(true);
	const [error, setError] = useState<Error | null>(null);
	const mediaClient = useMediaClient();

	useEffect(() => {
		let isMounted = true;
		const fetchDurations = async () => {
			if (!mediaFiles.length) {
				setMediaVideoDurations(0);
				setIsLoadingMediaDurations(false);
				return;
			}

			setIsLoadingMediaDurations(true);
			try {
				const durations = await mediaClient.file.getVideoDurations(
					mediaFiles.map((file) => ({ id: file, collectionName })),
				);
				const totalDuration = Object.values(durations).reduce((acc, duration) => acc + duration, 0);
				if (isMounted) setMediaVideoDurations(totalDuration * 1000); // convert to ms for consistency
			} catch (err) {
				if (isMounted) setError(err instanceof Error ? err : new Error('Unknown error'));
			}
			if (isMounted) setIsLoadingMediaDurations(false);
		};
		void fetchDurations();

		return () => {
			isMounted = false;
		};
	}, [mediaClient, mediaFiles, collectionName]);

	return {
		mediaVideoDurations,
		isLoadingMediaDurations,
		error,
	};
};

export const useLoadLoomVideoDurations = (videos: { id: string; url: string }[]) => {
	const [loomVideoDurations, setLoomVideoDurations] = useState(0);
	const [isLoadingLoomDurations, setIsLoadingLoomDurations] = useState(false);
	const [error, setError] = useState<Error | null>(null);
	const { cloudId, activationId } = useSessionData();

	useEffect(() => {
		let isMounted = true;

		const fetchAllDurations = async () => {
			if (!videos.length) {
				setIsLoadingLoomDurations(false);
				return;
			}

			setIsLoadingLoomDurations(true);
			setError(null);

			try {
				const client = getAGGClient();
				let totalDuration = 0;

				for (const video of videos) {
					if (!isMounted) break;

					// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
					const response = await client.query<LoomVideoDurationsQueryType>({
						query: LoomVideoDurationsQuery,
						variables: {
							id: getLoomARI({
								cloudId,
								activationId,
								videoId: video.id,
							}),
						},
					});

					if (!isMounted) break;

					if (response.data?.loom_videoDurations?.playableDuration) {
						totalDuration += response.data.loom_videoDurations.playableDuration;
					}
				}

				if (isMounted) {
					setLoomVideoDurations(totalDuration * 1000);
					setIsLoadingLoomDurations(false);
				}
			} catch (err) {
				if (isMounted) {
					setError(err instanceof Error ? err : new Error('Unknown error'));
					setIsLoadingLoomDurations(false);
				}
			}
		};

		void fetchAllDurations();

		return () => {
			isMounted = false;
		};
	}, [videos, cloudId, activationId]);

	return {
		loomVideoDurations,
		isLoadingLoomDurations,
		error,
	};
};
